import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { ContactsService } from '@shared/services/contacts.service';
import { CategoriesService } from '@shared/services/categories.service';
import { LocationsService } from '@shared/services/locations.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { DomSanitizer } from '@angular/platform-browser';
import { IPayload } from '@shared/interfaces/payload.interface';
import jwtDecode from 'jwt-decode';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { ICategory } from '@shared/interfaces/categories.interface';
import { IEmployee } from '@shared/interfaces/employee.interface';
import { EmployeesService } from '@shared/services/employees.service';
import { IUser } from '@shared/interfaces/user.interface';
import { NgxSelectOption } from 'ngx-select-ex/ngx-select/ngx-select.classes';
import { ILocation } from '@shared/interfaces/location.interface';

@Component({
  selector: 'app-modals-categories',
  templateUrl: './modals.categories.component.html',
  styleUrls: ['./modals.categories.component.scss']

})
export class ModalsCategoriesComponent
  extends ModalDirective<ICategory>
  implements OnInit {
  public title = String('CATEGORIES.ADD.TITLE');
  public description = String('CATEGORIES.ADD.DESCRIPTION');
  public config: any = {
    class: 'modal-lg'
  };
  public disableButtons = Boolean(false);
  public isEditing = Boolean(false);
  public isOpen = Boolean(false);
  public submitKey = String('BUTTONS.SUBMIT');
  public deleteKey: string;
  public subLocations: Partial<ILocation>[] = [];
  public isLoading = Boolean(false);
  public employees: Partial<IUser>[] = [];
  public subLocationsFilters: any[] = [
    {
      key: 'pagination',
      value: false
    }
  ];

  public entry: FormGroup;
  public assignedUsers: Partial<IUser>[];
  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _categories: CategoriesService,
    private readonly _logger: LoggerService,
    private readonly _fb: FormBuilder,
    private readonly _toasts: ToastService,
    public readonly _locations: LocationsService,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    public readonly _employees: EmployeesService,
    public dom: DomSanitizer
  ) {
    super();
    this.onGetValueFromAttributes = this.onGetValueFromAttributes.bind(this);
  }

  ngOnInit(): void {
    this.getSubLocations();

    this.openModal.subscribe((e: ICategory) => {
      this.isOpen = false;
      this.getEmployees();

      if (e) {
        this.title = 'CATEGORIES.EDIT.TITLE';
        this.description = 'CATEGORIES.EDIT.DESCRIPTION';
        this.deleteKey = 'BUTTONS.DELETE';
        this.isEditing = true;
        const subLocations = e.subLocations?.map((l: ILocation) => l.id) || [];
        this.entry.patchValue({ ...e, subLocations, responsibleUsers: e.responsibleUsers.map((user: IUser) => user.id) || [] });
      } else {
        this.title = 'CATEGORIES.ADD.TITLE';
        this.description = 'CATEGORIES.ADD.DESCRIPTION';
        this.deleteKey = null;
        this.isEditing = false;
      }

    });

    this.createForm();
  }

  ngOnDestroy() {
   
  }

  public whenModalClose(type: string): void {
    if (type === 'SUBMIT') {
      this.errors = [];
      this.entityForm.ngSubmit.emit();
    } else if (type === 'DELETE') {
      this.delete();
    }
  }

  public selectChangesLocations(e: NgxSelectOption[]) {
    const locations = e.map((ee: NgxSelectOption) => ee.value);
    this.entry.patchValue({ subLocations: locations });
  }

  
  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      let method = 'post';
      let url = `${method.toUpperCase()} /categories`;
      if (value.id) {
        method = 'patch';
        url = `${method.toUpperCase()} /categories/${value.id}`;
      }
      this.entry.disable();
      this.disableButtons = true;
      this._categories[method](value).subscribe(
        (res: ICategory) => {
          this._logger.info(this.constructorName, url, res);
          this._toasts.success('Category Saved!');
          this.entry.enable();
          this.disableButtons = false;
          this.resetModal();
          this.setDefaultValues();
          this.resetForm();
          this.closeModal.next();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
          if (err.errors) {
            this.errors = err.errors;
          }
          this.entry.enable();
          this.disableButtons = false;
          this._toasts.error('An error has occurred!');
        }
      );

    }
  }

  public onDismiss() {
    this.errors = [];

    this.entry?.reset();
    this.setDefaultValues();
  }

  private setDefaultValues() {
    this.disableButtons = false;
    this.entityForm.reset();
    this.errors = [];
    this.createForm();
    this.entry.enable();
  }

  private getEmployees() {
    const filters = [
      {
        key: 'pagination',
        value: 'false'
      }
    ];
    const url = 'GET /employees';
    this._employees.get(1, filters).subscribe(
      (res: IEmployee[]) => {
        this._logger.info(this.constructorName, url, res);
        this.employees = res.map((e: IEmployee) => e.user);
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  private getSubLocations() {
    const locationsFilters: any[] = [
      {
        key: 'pagination',
        value: 'false'
      },
      {
        key: 'onlySubLocations',
        value: 'true'
      }
    ];

    const url = `GET /locations`;
    this._locations.get(1, locationsFilters).subscribe(
      (res: ILocation[]) => {
        this._logger.info(this.constructorName, url, res);

        this.subLocations = res.map((l: ILocation) => ({
          id: l.id,
          label: l.label
        }));
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  public onGetValueFromAttributes(e: ICategory) {
    return e.name;
  }


  public selectChangesResponsibleUsers(e: any[]) {
    const responsibleUsers = e.map((ee: any) => ee.value);
    setTimeout(() => {
      this.entry.patchValue({ responsibleUsers });
    }, 500);
  }


  protected createForm() {
    this.entry = this._fb.group({
      id: [''],
      responsibleUsers: [[], []],
      subLocations: ['', Validators.required],
      name: ['', [Validators.required]],
    });
  }

  private delete() {
    const id = this.entry.get('id').value;
    const url = `DELETE /categories/${id}`;

    this._categories.delete(id).subscribe(
      (res: ICategory) => {
        this._logger.info(this.constructorName, url, res);

        this.resetModal();
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        if (err.errors) {
          this.errors = err.errors;
        }
      }
    );
  }
}
